import React, { useState, useEffect } from 'react';
import './App.css';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import CustomersList from './components/CustomersList';
import CustomerDetails from './components/CustomerDetails';
import CreateCustomer from './components/CreateCustomer';
import SubscriptionsList from './components/SubscriptionsList';
import SubscriptionDetails from './components/SubscriptionDetails';
import VPSList from './components/VPSList';
import VPSDetails from './components/VPSDetails';
import CreateVPS from './components/CreateVPS';
import LeadsList from './components/LeadsList';
import LeadDetails from './components/LeadDetails';
import { isAuthenticated, getAdminData, logout, apiRequest } from './utils/api';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(isAuthenticated());
  const [adminData, setAdminData] = useState(getAdminData());
  const [currentView, setCurrentView] = useState('dashboard');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [selectedVPS, setSelectedVPS] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [showCreateVPS, setShowCreateVPS] = useState(false);

  useEffect(() => {
    if (isLoggedIn) {
      fetchStats();
      if (currentView === 'customers') {
        fetchCustomers();
      }
    }
  }, [currentView, isLoggedIn]);

  const handleLoginSuccess = (data) => {
    setAdminData(data.admin);
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    logout();
    setIsLoggedIn(false);
    setAdminData(null);
  };

  const fetchStats = async () => {
    try {
      const data = await apiRequest('/admin/stats');
      if (data && data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const data = await apiRequest('/admin/customers');
      if (data && data.success) {
        setCustomers(data.data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
    setSelectedCustomer(null);
    setSelectedSubscription(null);
    setSelectedVPS(null);
    setSelectedLead(null);
    setShowCreateVPS(false);
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setCurrentView('customer-details');
  };

  const handleSubscriptionSelect = (subscription) => {
    setSelectedSubscription(subscription);
    setCurrentView('subscription-details');
  };

  const handleCustomerCreated = () => {
    fetchCustomers();
    setCurrentView('customers');
  };

  const handleVPSSelect = (vps) => {
    setSelectedVPS(vps);
    setCurrentView('vps-details');
  };

  const handleVPSCreated = () => {
    setShowCreateVPS(false);
    setCurrentView('vps');
  };

  const handleVPSUpdated = () => {
    setSelectedVPS(null);
    setCurrentView('vps');
  };

  const handleVPSDeleted = () => {
    setSelectedVPS(null);
    setCurrentView('vps');
  };

  const getPageTitle = () => {
    const titles = {
      'dashboard': 'لوحة التحكم',
      'customers': 'العملاء',
      'customer-details': 'تفاصيل العميل',
      'create-customer': 'إضافة عميل جديد',
      'subscriptions': 'الاشتراكات',
      'subscription-details': 'تفاصيل الاشتراك',
      'vps': 'خوادم VPS',
      'vps-details': 'تفاصيل الخادم',
      'leads': 'الطلبات',
      'lead-details': 'تفاصيل الطلب'
    };
    return titles[currentView] || 'لوحة التحكم';
  };

  // Show login page if not authenticated
  if (!isLoggedIn) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  const getAdminInitials = () => {
    if (!adminData) return 'A';
    const name = adminData.name || adminData.username || '';
    return name.charAt(0).toUpperCase();
  };

  return (
    <div className="app">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">T+</div>
          <div className="sidebar-title">TijaraPlus</div>
        </div>

        <nav className="sidebar-nav">
          <button
            className={`nav-link ${currentView === 'dashboard' ? 'active' : ''}`}
            onClick={() => handleViewChange('dashboard')}
          >
            <span className="nav-link-icon">📊</span>
            <span>لوحة التحكم</span>
          </button>

          <div className="nav-section">
            <div className="nav-section-title">إدارة العملاء</div>
            <button
              className={`nav-link ${currentView === 'customers' || currentView === 'customer-details' ? 'active' : ''}`}
              onClick={() => handleViewChange('customers')}
            >
              <span className="nav-link-icon">👥</span>
              <span>العملاء</span>
            </button>
            <button
              className={`nav-link ${currentView === 'create-customer' ? 'active' : ''}`}
              onClick={() => handleViewChange('create-customer')}
            >
              <span className="nav-link-icon">➕</span>
              <span>إضافة عميل</span>
            </button>
          </div>

          <div className="nav-section">
            <div className="nav-section-title">الاشتراكات</div>
            <button
              className={`nav-link ${currentView === 'subscriptions' || currentView === 'subscription-details' ? 'active' : ''}`}
              onClick={() => handleViewChange('subscriptions')}
            >
              <span className="nav-link-icon">📋</span>
              <span>قائمة الاشتراكات</span>
            </button>
          </div>

          <div className="nav-section">
            <div className="nav-section-title">البنية التحتية</div>
            <button
              className={`nav-link ${currentView === 'vps' || currentView === 'vps-details' || showCreateVPS ? 'active' : ''}`}
              onClick={() => handleViewChange('vps')}
            >
              <span className="nav-link-icon">🖥️</span>
              <span>خوادم VPS</span>
            </button>
          </div>

          <div className="nav-section">
            <div className="nav-section-title">الطلبات</div>
            <button
              className={`nav-link ${currentView === 'leads' || currentView === 'lead-details' ? 'active' : ''}`}
              onClick={() => handleViewChange('leads')}
            >
              <span className="nav-link-icon">📝</span>
              <span>قائمة الطلبات</span>
            </button>
          </div>
        </nav>

        <div className="sidebar-footer">
          <div className="admin-info">
            <div className="admin-avatar">{getAdminInitials()}</div>
            <div className="admin-details">
              <div className="admin-name">{adminData?.name || adminData?.username || 'المسؤول'}</div>
              <div className="admin-role">مدير النظام</div>
            </div>
          </div>
          <button className="logout-button" onClick={handleLogout}>
            تسجيل الخروج
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-wrapper">
        <header className="top-header">
          <h1 className="page-title">{getPageTitle()}</h1>
          <div className="header-actions">
            {/* Add any header actions here */}
          </div>
        </header>

        <div className="main-content">
          {currentView === 'dashboard' && <Dashboard stats={stats} onRefresh={fetchStats} />}
          {currentView === 'customers' && (
            <CustomersList
              customers={customers}
              onCustomerSelect={handleCustomerSelect}
              onRefresh={fetchCustomers}
            />
          )}
          {currentView === 'customer-details' && selectedCustomer && (
            <CustomerDetails
              customer={selectedCustomer}
              onBack={() => handleViewChange('customers')}
              onRefresh={fetchCustomers}
            />
          )}
          {currentView === 'create-customer' && (
            <CreateCustomer
              onCustomerCreated={handleCustomerCreated}
              onCancel={() => handleViewChange('customers')}
            />
          )}
          {currentView === 'subscriptions' && (
            <SubscriptionsList
              onSubscriptionSelect={handleSubscriptionSelect}
              onRefresh={() => {}}
            />
          )}
          {currentView === 'subscription-details' && selectedSubscription && (
            <SubscriptionDetails
              subscription={selectedSubscription}
              onBack={() => handleViewChange('subscriptions')}
              onRefresh={() => {
                handleViewChange('subscriptions');
              }}
            />
          )}
          {currentView === 'vps' && !showCreateVPS && (
            <VPSList
              onVPSSelect={handleVPSSelect}
              onCreateNew={() => setShowCreateVPS(true)}
            />
          )}
          {currentView === 'vps' && showCreateVPS && (
            <CreateVPS
              onVPSCreated={handleVPSCreated}
              onCancel={() => setShowCreateVPS(false)}
            />
          )}
          {currentView === 'vps-details' && selectedVPS && (
            <VPSDetails
              vps={selectedVPS}
              onBack={() => handleViewChange('vps')}
              onVPSUpdated={handleVPSUpdated}
              onVPSDeleted={handleVPSDeleted}
            />
          )}
          {currentView === 'leads' && (
            <LeadsList
              onLeadSelect={(lead) => {
                setSelectedLead(lead);
                setCurrentView('lead-details');
              }}
              onCreateNew={() => {}}
            />
          )}
          {currentView === 'lead-details' && selectedLead && (
            <LeadDetails
              lead={selectedLead}
              onBack={() => handleViewChange('leads')}
              onRefresh={() => {
                handleViewChange('leads');
              }}
            />
          )}
        </div>
      </main>
    </div>
  );
}

export default App;
